import { IConfig } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import * as React from "react";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaEntitySwitcher from "../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import { KuikaEditorIdentity, KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { CONFIG_NAME_MAX_LENGTH, configNameRules, configTestName } from "./config-manager-model";

/**
 * The configuration editor's header band: the configuration's name (which is also the
 * rename control and the switcher), the back control, and SAVE.
 *
 * Shared by both editors — the project's `ConfigContainer` and the workspace's
 * `GlobalConfigsContainer` — which is the point: the two hand-rolled headers it replaces
 * drew the same thing with different glyphs, different hex fills and different spacing.
 *
 * Presentation only. Every handler belongs to the container, which owns the form, the
 * dirty hash and the unsaved-changes handshake. There is no CANCEL: it ran the same close
 * path the back arrow does, so it carried the id the suites target rather than a second
 * control. SAVE stays — unlike the API Builder, a configuration is a large form saved as a
 * whole rather than a name plus a body that writes itself.
 *
 * The title must render INSIDE the container's antd `Form` — the field is `name`, which
 * `loadConfig` seeds and `handleSubmit` reads back.
 */

export interface IConfigEditorHeaderProps {
  configName: string;
  isNameOnEdit: boolean;
  onStartRename: () => void;
  /** Called once the field's rules pass; the container commits the name to its state. */
  onSubmitName: () => void;
  onAbortRename: () => void;
  /** The list the rename's uniqueness check runs against; the container excludes read-only global reference rows. */
  nameScope: IConfig[];
  /** Excludes the open configuration from its own uniqueness check. */
  selfId?: string;
  /** The configuration's saved name — an unchanged name stays saveable even if it now violates the rule. */
  originalName?: string;
  /** Runs the field's rules and resolves true when the value is accepted. */
  validateName: () => Promise<boolean>;

  /** The configurations the switcher can move to. Empty hides the switcher entirely. */
  configs: IConfig[];
  currentConfig?: IConfig;
  onSwitchConfig: (config: IConfig) => void;

  /** Leaving the editor. The bar's back control draws the only control for it. */
  onBack: () => void;
  /** The configuration list's own row actions, for the switcher's "…". */
  rowActions?: KuikaListTableRowAction<IConfig>[];
  /** SAVE while creating, UPDATE while editing — the label both editors already used. */
  saveLabel: string;
  /** The `<form>` the submit button belongs to, since the button sits outside it. */
  formId: string;
  /**
   * Where the save stands (`KuikaButton` `status`): the label rolls to "Saving", then "Saved" or
   * "Try again", and the button's width follows it. The editor closes onto the landing after a
   * save, so the ✓ here is the confirmation the reader sees first — the container's
   * `EditorSaveStatus` holds it before the close. Omitted, SAVE is the plain submit it always was.
   */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  /**
   * The platform tabs, centred in the header row.
   *
   * They had a row of their own under the header; folding them in gives the panes back the
   * height, and the tabs belong to the whole editor rather than to either end of it.
   */
  tabs?: React.ReactNode;

  editIconTestId: string;
  submitIconTestId: string;
  nameInputTestId: string;
  nameTestId?: string;
  /** Goes on the back arrow, which is the close control now. */
  cancelTestId: string;
  saveTestId: string;
}

export class ConfigEditorHeader extends React.PureComponent<IConfigEditorHeaderProps> {
  /**
   * Validation is awaited rather than read off the last result. `configNameRules`'s
   * uniqueness check is async and the field is freshly mounted each time the pencil is
   * clicked, so `getFieldError` is empty on the first Enter and would let a duplicate
   * through — which is exactly what the pre-refactor `handleSubmitNameChange` did. An
   * invalid name still fails silently; the field's own message is the feedback, as before.
   */
  handleSubmitName = async (): Promise<void> => {
    const isValid: boolean = await this.props.validateName();
    if (!isValid) return;
    this.props.onSubmitName();
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") return;
    // Stops the antd Form from submitting the whole configuration and keeps the designer's
    // global key handlers from seeing this Enter.
    e.stopPropagation();
    e.preventDefault();
    void this.handleSubmitName();
  };

  /**
   * A configuration being created is not in the list yet, so there is nothing to switch
   * between until it has been saved — it names itself instead.
   */
  renderIdentity = (): React.ReactNode => {
    const icon: React.ReactNode = <KuikaSvg name="config-manager" type="topbar-icons" width={16} height={16} />;

    if (this.props.configs.length === 0) {
      return <KuikaEditorIdentity icon={icon} name={this.props.configName} nameTestId={this.props.nameTestId} />;
    }

    return (
      <KuikaEntitySwitcher<IConfig>
        trigger="chip"
        triggerIcon={icon}
        triggerLabel={this.props.configName}
        triggerLabelTestId={this.props.nameTestId}
        items={this.props.configs}
        current={this.props.currentConfig}
        onSelect={this.props.onSwitchConfig}
        // Creating starts on the landing page, which the back arrow already reaches;
        // a "+" here would be a second, less obvious route to the same place.
        entityLabel="Configuration"
        iconName="config-manager"
        iconType="topbar-icons"
        triggerTestId="configSwitcherTrigger"
        itemTestId={(config: IConfig) => `${configTestName(config)}SwitcherItem`}
        rowActions={this.props.rowActions}
        onManageAll={this.props.onBack}
      />
    );
  };

  renderTitle = (): React.ReactNode => (
    <KuikaEditorTitle
      className="kuika-config-editor__title"
      entityLabel="configuration"
      identity={this.renderIdentity()}
      isRenaming={this.props.isNameOnEdit}
      // An as-yet-unnamed configuration has nothing to abort back to, so no cancel.
      canAbortRename={!!this.props.configName}
      onStartRename={this.props.onStartRename}
      onConfirmRename={this.handleSubmitName}
      onAbortRename={this.props.onAbortRename}
      editTestId={this.props.editIconTestId}
      confirmTestId={this.props.submitIconTestId}
      renameField={
        <Form.Item
          name="name"
          className="kuika-config-editor__title-field"
          rules={configNameRules({
            configs: this.props.nameScope,
            selfId: this.props.selfId,
            originalName: this.props.originalName
          })}
        >
          <KuikaInput
            size="md"
            data-testid={this.props.nameInputTestId}
            onKeyDown={this.handleKeyDown}
            placeholder="Config Name"
            autoFocus
            maxLength={CONFIG_NAME_MAX_LENGTH}
          />
        </Form.Item>
      }
    />
  );

  render() {
    return (
      <KuikaPageHeader
        variant="bar"
        title={this.props.configName || "Configuration"}
        onBack={this.props.onBack}
        backLabel="Back to Config Manager"
        // Back is the only way out of the editor now, so it carries the id the suites used
        // to resolve on the CANCEL button it replaced.
        backTestId={this.props.cancelTestId}
        renderTitle={this.renderTitle}
        center={this.props.tabs}
        actions={
          <KuikaButton
            data-testid={this.props.saveTestId}
            type="primary"
            size="md"
            uppercase
            htmlType="submit"
            form={this.props.formId}
            status={this.props.status}
            statusLabels={this.props.statusLabels}
          >
            {this.props.saveLabel}
          </KuikaButton>
        }
      />
    );
  }
}
